import { DateTime, Interval } from 'luxon';
import { FC, useMemo } from 'react';

import { WorkflowRunStepStatsCard } from 'dashboard/components-lib/WorkflowRunStepStats';
import { humanReadableRunTime, startEndTime } from 'dashboard/lib/workflows/run-stats';
import { WorkflowRunStep, WorkflowRunStepStatus } from 'dashboard/models/workflow-step';
import { FakeWorkflowStepType, WorkflowStepType } from 'dashboard/models/workflow-version';
import { TimelineItem } from 'dashboard/views/DashboardWorkflowRunsShowView/TimelineItem';
import { intervalPercentile } from 'dashboard/views/DashboardWorkflowRunsShowView/utils';
import WorkflowRunStepTimeline from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowRunStepTimeline';
import { CommonTimelineProps } from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowRunTimeline';
import useToggle from 'lib/hooks/useToggle';

interface WorkflowRunBlockTimelineProps {
  id: string;
  indent?: number;
  index?: number;
  type?: WorkflowStepType | FakeWorkflowStepType;
  visible?: boolean;
  steps: WorkflowRunStep[];
  setIsResizingColumns: (isResizing: boolean) => void;
  totalInterval: Interval;
  name?: string;
  flexibleColumnWidth: number;
}

const WorkflowRunBlockTimeline: FC<WorkflowRunBlockTimelineProps & CommonTimelineProps> = ({
  tokensToSteps,
  tokensToRunSteps,
  allRunSteps,
  indent = 0,
  visible = true,
  totalInterval,
  searchString,
  activeNode,
  viewMode,
  selectNode,
  setIsResizingColumns,
  updateLoopCycle,
  id,
  type,
  index = 0,
  steps,
  name = 'Unknown',
  flexibleColumnWidth,
}) => {
  const [expandChildren, { toggle: toggleChildren }] = useToggle(
    type !== WorkflowStepType.Loop && type !== WorkflowStepType.Parallel,
  );

  const status = useMemo(() => {
    if (steps.some((step) => step.attributes.status === WorkflowRunStepStatus.InProgress)) {
      return WorkflowRunStepStatus.InProgress;
    }

    if (
      steps.some((step) =>
        [WorkflowRunStepStatus.Failed, WorkflowRunStepStatus.Errored].includes(
          step.attributes.status,
        ),
      )
    ) {
      return WorkflowRunStepStatus.Errored;
    }

    return WorkflowRunStepStatus.Completed;
  }, [steps]);

  const { startTime, endTime, durationString } = useMemo(() => {
    if (steps.length === 0) {
      return {
        startTime: DateTime.now(),
        endTime: DateTime.now(),
        durationString: '',
      };
    }

    const { startTime, endTime } = startEndTime(steps);

    return {
      startTime,
      endTime,
      durationString: humanReadableRunTime(startTime, endTime).durationString,
    };
  }, [steps]);

  const { startFraction, spanFraction } = useMemo(() => {
    if (steps.length === 0) {
      return { startFraction: 0, spanFraction: 0 };
    }

    const { startTime, endTime } = startEndTime(steps);

    return intervalPercentile(Interval.fromDateTimes(startTime, endTime), totalInterval);
  }, [steps, totalInterval]);

  if (steps.length === 0) {
    return <></>;
  }

  return (
    <>
      {type === WorkflowStepType.Loop && (
        <TimelineItem
          id={`${id}__loop-marker-${index}`}
          name={`Loop ${index + 1}`}
          duration={durationString}
          indent={indent}
          childrenVisible={expandChildren}
          visible={visible && searchString.length === 0} // never show fake item while searching
          startFraction={startFraction}
          lengthFraction={spanFraction}
          flexibleColumnWidth={flexibleColumnWidth}
          viewMode={viewMode}
          status={status}
          tooltip={
            <WorkflowRunStepStatsCard
              manualInput={{
                name: `${name} - Loop ${index + 1}`,
                status: status,
                startTime: startTime,
                endTime: endTime,
              }}
            />
          }
          onSelect={toggleChildren}
          toggleChildren={toggleChildren}
          setIsResizingColumns={setIsResizingColumns}
        />
      )}
      {type === WorkflowStepType.Parallel && (
        <TimelineItem
          id={`${id}__parallel-marker-${index}`}
          name={`Branch ${index + 1}`}
          duration={durationString}
          indent={indent}
          childrenVisible={expandChildren}
          visible={visible && searchString.length === 0}
          startFraction={startFraction}
          lengthFraction={spanFraction}
          flexibleColumnWidth={flexibleColumnWidth}
          viewMode={viewMode}
          status={status}
          tooltip={
            <WorkflowRunStepStatsCard
              manualInput={{
                name: `${name} - Branch ${index + 1}`,
                status: status,
                startTime: startTime,
                endTime: endTime,
              }}
            />
          }
          onSelect={toggleChildren}
          toggleChildren={toggleChildren}
          setIsResizingColumns={setIsResizingColumns}
        />
      )}
      {steps
        .filter(
          (child) =>
            ![WorkflowRunStepStatus.Canceled, WorkflowRunStepStatus.Initialized].includes(
              child['attributes'].status,
            ),
        )
        .map((child) => (
          <WorkflowRunStepTimeline
            key={`workflow-run-diagnostics-step__${child.id}`}
            tokensToSteps={tokensToSteps}
            tokensToRunSteps={tokensToRunSteps}
            step={child}
            visible={visible && expandChildren}
            allRunSteps={allRunSteps}
            indent={indent}
            totalInterval={totalInterval}
            searchString={searchString}
            flexibleColumnWidth={flexibleColumnWidth}
            viewMode={viewMode}
            activeNode={activeNode}
            selectNode={selectNode}
            setIsResizingColumns={setIsResizingColumns}
            updateLoopCycle={updateLoopCycle}
          />
        ))}
    </>
  );
};

export default WorkflowRunBlockTimeline;
